import React, { useState, useEffect } from 'react';
import { INSURANCE_TYPE, DOCUMENT_TYPE } from '../utils/insuranceTypeUtils';
import { hasEnabledNonDocumentTypeAll } from '../utils/documentTypeUtils';

const DocumentTypeSelect = ({ documentType, setDocumentType, insuranceType, documentTypes = [], isHistory = false }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // 画面サイズの変更を監視
  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 768);
      }, 300);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const handleTooltipToggle = () => {
    if (isMobile) {
      setShowTooltip(!showTooltip);
    }
  };
  const getTooltipText = () => {
    if (insuranceType === 1) {
      // 生命保険
      return (
        <div className="c-tooltip-content">
          <p>
            <strong>しおり・約款</strong>
            <span>商品のしくみ、保障内容(支払事由)、免責事由など</span>
          </p>
          <p>
            <strong>新契約関連</strong>
            <span>引受上の諸条件、商品規程(限度額等)、新契約成立までの流れなど</span>
          </p>
          <p>
            <strong>保全・収納・支払関連</strong>
            <span>住所変更や未入金対応、保険金請求等の事務手続きなど</span>
          </p>
          <p>
            <strong>引受目安</strong>
            <span>疾病別の引受基準の目安など</span>
          </p>
        </div>
      );
    } else if (insuranceType === 2) {
      // 損害保険
      return (
        <div className="c-tooltip-content c-tooltip-popup--small">
          <p>
            <strong className='small'>しおり・約款</strong>
            <span>商品のしくみ、保障内容(支払事由)、免責事由など</span>
          </p>
          <p>
            <strong className='small'>ハンドブック</strong>
            <span>引受上の諸条件、商品規程(限度額等)、新契約成立までの流れなど</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // 「すべて」以外に有効な文書種別が1つ以上あるかを判定
  // insuranceType でフィルタすることで、「指定なし」エントリや他保険種別の種別を除外する
  const hasEnabledNonAll = hasEnabledNonDocumentTypeAll(
    documentTypes.filter(type =>
      type.insurance_type && Number(type.insurance_type) === Number(insuranceType)
    )
  );

  // 全文書種別が無効（アクセス不可）かを判定
  // 合成エントリの all は常に is_enabled: true のため、all を除いた種別で判定する
  const allDisabled = !hasEnabledNonAll;

  // ✅ 選択変更時のハンドラー - is_enabledをチェック
  const handleSelectChange = (e) => {
    const selectedCode = e.target.value;
    
    // 早期リターン: 全無効時は「すべて」への変更を許可しない
    if (selectedCode === DOCUMENT_TYPE.ALL && allDisabled) {
      return;
    }
    
    // 通常の選択肢の場合、有効性をチェック
    if (selectedCode !== DOCUMENT_TYPE.ALL) {
      const selectedType = documentTypes.find(type => type.code === selectedCode);
      if (!selectedType || !selectedType.is_enabled) {
        return;
      }
    }
    
    setDocumentType(selectedCode);
  };

  const tooltipContent = getTooltipText();
  return (
    <div>
      {!isHistory && (
        <div
          className="c-question-icon-wrapper"
          tabIndex={0}
          onMouseEnter={() => !isMobile && setShowTooltip(true)}
          onMouseLeave={() => !isMobile && setShowTooltip(false)}
          onClick={handleTooltipToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleTooltipToggle();
            }
          }}
        >
          <button
            className="c-question-icon"
            onClick={handleTooltipToggle}
            aria-label="ドキュメントタイプのヘルプ"
            aria-expanded={showTooltip}
            type="button"
          >
            ?
          </button>
          {showTooltip && (
            <div
              className="c-tooltip-popup" role="tooltip" aria-live="polite" aria-label="書類種類の説明"
            >
              {tooltipContent}
              {isMobile && (
                <button
                  className="c-tooltip-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTooltip(false);
                  }}
                  aria-label="ヘルプを閉じる"
                  type="button"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <div className="select-container select-box ver04 selection-option first-mb">
        <select
          className="document-type-select solicitor-select"
          value={documentTypes.length === 0 ? '' : documentType}
          onChange={handleSelectChange}
          disabled={documentTypes.length === 0}
        >
          {documentTypes.length === 0 ? (
            <option value="">読み込み中...</option>
          ) : (
            <>
              {/* アクセス不可の文書種別は選択不可にする */}
              {/* 「すべて」は自身の is_enabled ではなく、他に有効な種別があるかで判定する */}
              {documentTypes.map((type) => {
                const isAllType = type.code === DOCUMENT_TYPE.ALL;
                const isDisabled = isAllType ? !hasEnabledNonAll : !type.is_enabled;
                return (
                  <option key={type.code} value={type.code} disabled={isDisabled}>
                    {isAllType
                      ? (insuranceType === INSURANCE_TYPE.LIFE ? 'すべて(引受目安以外)' : 'すべて')
                      : type.name || type.code}
                  </option>
                );
              })}
            </>
          )}
        </select>
      </div>
    </div>
  );
};

export default DocumentTypeSelect;
